import { atom } from "jotai";
import { atomWithStorage } from "jotai/utils";

import { MARQUE } from "@dozka/ui";

/**
 * Cockpit-wide client state. Persisted atoms survive reload; transient atoms don't.
 *
 * Naming convention :
 *   - cockpit:* keys are reserved for cockpit shell state in localStorage
 *   - panel-local state should use its own atoms colocated in the panel folder
 *
 * Note Phase 4 : l'ancien `activeModeAtom` (concept "mode") a ete remplace par
 * `activeWorkspaceIdAtom` qui vit dans `@/shell/workspaces/atoms.ts`. Pour
 * acceder au workspace actif, importer depuis `@/shell/workspaces`.
 */

/** UI theme — pilote `.dark` + l'attribut `data-theme` sur <html>.
 *  "glass" = variante dark TOUT translucide (verre dépoli sur fond d'écran).
 *
 *  🎨 Le défaut est « glass », et la clé est en `:v2` — POUR TOUT LE MONDE.
 *  Kayla, 09-09-2026 : « j'aimerais vraiment changer le thème par défaut pour
 *  tout le monde, pour les gens qui l'utilisent et pour les prochains. » Un
 *  défaut ne se voit que de qui n'a jamais choisi ; changer la clé fait
 *  oublier le choix d'avant, donc les comptes existants repartent aussi sur le
 *  verre. On peut toujours re-choisir dans « Personnaliser le thème ». */
export const themeAtom = atomWithStorage<"light" | "dark" | "glass">(
  "cockpit:theme:v2",
  // « glass » chez Dozka ; la marque d'un autre domaine (RishFans : noir) pose
  // le sien — le choix d'un utilisateur, lui, prime toujours.
  MARQUE.themeParDefaut,
);

/**
 * Couleur de marque (accent) personnalisée par l'utilisateur. `null` = défaut
 * du thème (#3b82f6, le bleu). Appliquée sur <html> par `ThemeProvider` via
 * `applyBrandColor` (cf core/theme/brandColor.ts) → recolore tous les `*-mark`.
 *
 * `:v3` pour la même raison que le thème : le bleu est devenu le défaut le
 * 12-09-2026 (Kayla : « tu mets du bleu à la place du vert »), et le vert
 * choisi ou hérité avant devait le masquer sur tous les postes déjà
 * installés — comme l'orange masquait le vert le 09-09. Le thème, lui, ne
 * bouge pas : le verre reste le défaut.
 */
export const brandColorAtom = atomWithStorage<string | null>(
  "cockpit:brand-color:v3",
  null,
);

/**
 * Taille d'affichage du cockpit, en pourcent — l'equivalent interne du zoom
 * navigateur. Appliquee sur <body> par `ThemeProvider` via
 * `appliquerTailleAffichage` (cf core/theme/tailleAffichage.ts, qui explique
 * pourquoi c'est <body> et pas <html>).
 *
 * PAR APPAREIL, pas par compte : ce qui varie c'est l'echelle Windows de la
 * machine, pas la personne. "auto" (le defaut) vise 1920 px utiles sans jamais
 * agrandir ni descendre sous 70 % ; un nombre = choisi a la main.
 */
export const tailleAffichageAtom = atomWithStorage<number | "auto">(
  "cockpit:taille-affichage:v1",
  "auto",
);

/** Sidebar collapsed/expanded state (mobile + desktop). */
export const sidebarOpenAtom = atomWithStorage<boolean>("cockpit:sidebar-open", true);

/** Set when the user has rearranged panels away from the active workspace preset. */
export const layoutDirtyAtom = atom<boolean>(false);

/** PerfHUD overlay (?debug=1 also sets this). */
export const debugHudAtom = atomWithStorage<boolean>("cockpit:debug-hud", false);

/**
 * Scope cross-account du cockpit (2026-05-27).
 *
 * Tag union :
 *   - { kind: "single", accountId } — vue d'une seule org (mode legacy).
 *   - { kind: "all" }                — agrégation cross-org de tous les
 *                                      accounts où le user est membre.
 *
 * Persisté pour que le choix survive aux reload. Le default est "single" sur
 * le 1er account, géré côté code par `useResolvedAccountScope` (pas dans
 * l'atom direct pour éviter la dépendance Convex circular).
 *
 * IMPORTANT : ce n'est PAS l'accountId actif. Les panneaux qui supportent
 * la vue "all" doivent brancher leurs queries via le hook adapté
 * (cf creators/atoms.ts useCreatorsList). Les panneaux qui ne supportent
 * PAS encore "all" doivent retomber sur le 1er account du user (fallback
 * non-cassant).
 */
export type AccountScope =
  | { kind: "single"; accountId: string }
  | { kind: "all" };

export const activeAccountScopeAtom = atomWithStorage<AccountScope | null>(
  "cockpit:active-account-scope:v1",
  null,
);

/**
 * ID du TabGroup currently focused. Set via click sur un tab ou sur le body
 * d'un group. Lu par les keyboard shortcuts (ex: Cmd+Alt+→ next tab) et le
 * PanelPicker (ajoute un panel dans CE group plutot que l'active-group fallback).
 *
 * Transient — pas persiste. Reset au reload.
 */
export const focusedGroupIdAtom = atom<string | null>(null);

/** Ouverture du PanelPicker (search par nom — accessible via burger menu). */
export const panelPickerOpenAtom = atom<boolean>(false);

/**
 * Instance ID du tab visible en mode mobile (single-panel shell).
 *
 * Transient (pas localStorage) — au reload on retombe sur le premier panel.
 * Distinct du `activeIndex` interne d'un TabGroup desktop, car en mobile on
 * rend un TabGroup virtuel issu de `flattenLayoutForMobile` (DFS du tree
 * desktop) — indexer dessus serait fragile car l'ordre DFS peut changer apres
 * un remove. On track par instanceId (stable a travers les reorders).
 *
 * Ecrit depuis MobileShell (clic sur tab) et PanelPicker (apres addPanel
 * pour auto-focus le nouveau panel). Lu par MobileShell pour activeIndex
 * passe a flattenLayoutForMobile.
 */
export const mobileActiveInstanceIdAtom = atom<string | null>(null);

/** Ouverture du LayoutPickerModal (Cmd+Shift+P + bouton "Disposition" TopBar). */
export const layoutPickerOpenAtom = atom<boolean>(false);

/** Ouverture de la SettingsModal (ouvert via le menu user de la TopBar). */
export const settingsModalOpenAtom = atom<boolean>(false);

/**
 * Profil utilisateur côté client. Mock tant que l'auth n'est pas branchée.
 * Persiste en localStorage pour simuler un compte qui survit au reload.
 *
 * Quand l'auth sera la, ces valeurs viendront du backend ; cet atom restera
 * comme cache local optimiste.
 */
export interface UserProfile {
  pseudo: string;
}

export const userProfileAtom = atomWithStorage<UserProfile>("cockpit:user-profile:v1", {
  pseudo: "user",
});

/**
 * Statut du bootstrap WorkOS → Convex (UserSync).
 *
 *   "idle"    — pas encore lancé (user vient d'arriver)
 *   "running" — fetch /organization_memberships en cours
 *   "done"    — terminé (succès ou no-op idempotent)
 *   "error"   — échec WorkOS API
 *
 * Lu par l'OrgGate : tant qu'on n'est pas "done" on garde l'écran de
 * loading ; une fois "done" on regarde `accounts.listMine` pour décider
 * shell vs onboarding.
 */
export type BootstrapStatus = "idle" | "running" | "done" | "error";

export const bootstrapStatusAtom = atom<BootstrapStatus>("idle");
